import { Box, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { KTag } from '@/ui-library/components';
import { TransferCandidate } from '@/assets/icons/ats';
import { ApplicationData } from '@/components/candidate-job-slider-v1/types/application.interface';
import { selectUsers } from '@/store/users/selectors';
import { useSelector } from 'react-redux';
import { useDateFormat } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { formatTransferDateTime } from '@/components/candidate-job-slider-v1/transferInfo';

// `${by} on ${date+time}` suffix; date+time render in the user's timezone and preference.
const suffix = (
  by: string | undefined,
  at: string | null | undefined,
  fmt: string,
  tz?: string
) =>
  `${by ? ` by ${by}` : ''}${at ? ` on ${formatTransferDateTime(at, fmt, tz)}` : ''}`;

// "Transfer history" block for multi-hop chains. Steps arrive most-recent-first, which is the
// display order we want (latest hop on top), rendered as an unordered (bulleted) list.
const buildHistoryBlock = (
  steps: { job?: string; by?: string; at?: string | null }[],
  fmt: string,
  tz?: string
) =>
  `Transfer history\n\n${steps
    .map(
      (step) =>
        `• Transferred from ${step.job || 'a job'}${suffix(step.by, step.at, fmt, tz)}`
    )
    .join('\n')}`;

export interface ReportsTransferChainHop {
  application_id?: number;
  job_id?: number;
  job_name?: string;
  transferred_by?: { id?: number; name?: string };
  transferred_at?: string;
}

export interface ReportsTransferredBadgeProps {
  type: 'from' | 'to';
  jobName: string;
  transferredBy?: { name?: string };
  transferredAt?: string;
  // Full backward chain (origin-first) for transfer destinations — renders the multi-line
  // "Transfer history". When absent, falls back to the single most-recent step.
  chain?: ReportsTransferChainHop[];
}

export const ReportsTransferredBadge = ({
  type,
  jobName,
  transferredBy,
  transferredAt,
  chain,
}: ReportsTransferredBadgeProps) => {
  const { momentDateTimeFormat, currUserTimezone } = useDateFormat();
  const { tNLevelTransfer } = useKFlags();
  const tooltipLabel = (() => {
    // Until n-level transfer is enabled, retain master's dateless single-step copy.
    if (!tNLevelTransfer) {
      const byLabel = transferredBy?.name ? ` by ${transferredBy.name}` : '';
      return type === 'to'
        ? `Transferred to ${jobName || 'another job'}${byLabel}`
        : `Transferred from ${jobName || 'this job'}${byLabel}`;
    }
    if (type === 'to') {
      return `Candidate transferred to ${jobName || 'another job'}${suffix(transferredBy?.name, transferredAt, momentDateTimeFormat, currUserTimezone)}`;
    }
    // Reserve the multi-line "Transfer history" for genuine multi-hop chains; a single hop
    // renders the clean single line (consistent with the in-app badge), not a 1-item history.
    if (chain && chain.length > 1) {
      return buildHistoryBlock(
        chain.map((hop) => ({
          job: hop.job_name,
          by: hop.transferred_by?.name,
          at: hop.transferred_at,
        })),
        momentDateTimeFormat,
        currUserTimezone
      );
    }
    return `Transferred from ${jobName || 'this job'}${suffix(transferredBy?.name, transferredAt, momentDateTimeFormat, currUserTimezone)}`;
  })();

  return (
    <Tooltip label={tooltipLabel} hasArrow whiteSpace="pre-line">
      <Box
        as="span"
        cursor="pointer"
        flexShrink={0}
        display="flex"
        mr="5px"
        mt="1px"
      >
        <KTag
          value={
            <Flex alignItems="center" gap="6px">
              <Icon as={TransferCandidate} w="16px" h="16px" />
            </Flex>
          }
          p="2px 8px"
          borderRadius="8px"
          bg="pastel-6-light"
          color="pastel-6-dark"
        />
      </Box>
    </Tooltip>
  );
};

export interface TransferredBadgeProps {
  transferInfo: ApplicationData['transfer_info'];
  showTooltipToLabel?: boolean;
  isReferredForm?: boolean;
  isIconOnly?: boolean;
  // When set, the tooltip describes only this item's source job (the application it
  // belongs to) instead of the whole transfer chain. Falls back to the full chain
  // when the id can't be resolved.
  sourceApplicationId?: number | string;
}

interface TransferStep {
  fromJob: string;
  toJob: string;
  by: string;
  at?: string;
}

export const TransferredBadge = ({
  transferInfo,
  showTooltipToLabel = false,
  isReferredForm = false,
  sourceApplicationId,
}: TransferredBadgeProps) => {
  const users = useSelector(selectUsers);
  const { momentDateTimeFormat, currUserTimezone } = useDateFormat();
  const { tNLevelTransfer } = useKFlags();
  if (!transferInfo) {
    return null;
  }
  const { history = [], applications = {} } = transferInfo;
  // @ts-expect-error - transferred_from is the referral form structure, not in the base transfer_info type
  const transferredFrom = transferInfo?.transferred_from as
    | {
        job: { id: number; name: string };
        referral_id: number;
        transferred_at: string;
        transferred_by_id: number;
      }
    | undefined;

  // Only the referral-form tooltip needs this lookup; skip the O(n) scan otherwise.
  const transferredBy = isReferredForm
    ? users.find((user) => user.id === transferredFrom?.transferred_by_id)
    : undefined;

  // Build transfer chain from history
  const transferSteps: TransferStep[] = [];

  history.forEach((appId) => {
    const app = applications[appId];
    if (app?.transferred_out) {
      transferSteps.push({
        fromJob: app.application?.job?.name || '',
        toJob:
          applications[app.transferred_out.to_application_id]?.application?.job
            ?.name || '',
        by: app.transferred_out.by?.name || '',
        at: app.transferred_out.at,
      });
    }
  });

  // When a specific item's source application is supplied, describe only that job
  // instead of the whole chain. Unresolved ids fall through to the chain tooltip.
  // Gated behind n-level transfer: until enabled, retain master's candidate-level copy.
  const sourceApp =
    tNLevelTransfer && sourceApplicationId
      ? applications[sourceApplicationId] ||
        applications[String(sourceApplicationId)]
      : undefined;
  const sourceStep = sourceApp
    ? {
        fromJob: sourceApp.application?.job?.name || '',
        toJob:
          applications[sourceApp.transferred_out?.to_application_id]
            ?.application?.job?.name || '',
        by: sourceApp.transferred_out?.by?.name || '',
        at: sourceApp.transferred_out?.at,
      }
    : null;

  const historyBlock = buildHistoryBlock(
    transferSteps.map((step) => ({
      job: step.fromJob,
      by: step.by,
      at: step.at,
    })),
    momentDateTimeFormat,
    currUserTimezone
  );

  const tooltipLabel = isReferredForm
    ? `Candidate transferred from the ${transferredFrom?.job?.name} job by ${transferredBy?.name}`
    : sourceStep
      ? // Per-item: this item's own source job, with date+time.
        `Transferred from ${sourceStep.fromJob}${suffix(sourceStep.by, sourceStep.at, momentDateTimeFormat, currUserTimezone)}`
      : transferSteps.length > 1
        ? historyBlock
        : transferSteps.length === 1
          ? // Single hop: unchanged copy.
            `Candidate transferred from the ${transferSteps[0].fromJob} job by ${transferSteps[0].by}`
          : 'Candidate transferred';

  const tooltipTolabel = sourceStep
    ? `Transferred to ${sourceStep.toJob}${suffix(sourceStep.by, sourceStep.at, momentDateTimeFormat, currUserTimezone)}`
    : transferSteps.length > 1
      ? historyBlock
      : transferSteps.length === 1
        ? `Candidate transferred to the ${transferSteps[0].toJob} job by ${transferSteps[0].by}`
        : 'Candidate transferred';

  return (
    <Tooltip
      label={showTooltipToLabel ? tooltipTolabel : tooltipLabel}
      hasArrow
      whiteSpace="pre-line"
    >
      <Box
        as="span"
        cursor="pointer"
        flexShrink={0}
        display="flex"
        mr="5px"
        mt="1px"
      >
        <KTag
          value={
            <Flex alignItems="center" gap="6px">
              <Icon as={TransferCandidate} w="16px" h="16px" />
            </Flex>
          }
          p={'2px !important'}
          borderRadius={'4px !important'}
          bg="pastel-6-light"
          color="pastel-6-dark"
        />
      </Box>
    </Tooltip>
  );
};
